/*
 * Copyright (c) 2022. American Express Company
 */

// third-party
import _ from 'lodash';
import Konva from 'konva';
import { IFrame } from 'konva/lib/types';
import { Group } from 'konva/lib/Group';
import { Rect } from 'konva/lib/shapes/Rect';
import { Line } from 'konva/lib/shapes/Line';
import { Circle } from 'konva/lib/shapes/Circle';
import { Shape } from 'konva/lib/Shape';
// first-party
import { TLElement } from './TLElement';
import { JaegerBarHeights, JaegerService } from '../../../services/jaeger.service';
import FormatService from '../../../services/format.service';
import ProjectJaeger from '../ProjectJaeger';
import { CashFlowModel } from 'src/types/cash-flow.model';
import TLBarColumn from './TLBarColumn';
import TLBar from './TLBar';
// styles, images, and constants
import infoIcon from '../info.svg';
import colours from 'src/styles/variables.module.scss';
import TimelineService from '../services/timeline.service.singleton';
import BarFactory from '../factories/bar.factory.singleton';
import {
    BOTTOM_MAX_TEXT_OFFSET,
    TIMELINE_FONT_SIZE_SMALL,
    TIMELINE_LEGEND_FONT,
    TOP_LEGEND_OFFSET,
    TOP_MAX_TEXT_OFFSET
} from 'src/utils/constants';
import { JaegerEvent } from '../data/JaegerEvent.enum';

export default class TLBarArea extends TLElement {
    public barAreaGroup: Group;
    public jaegerCashFlow: CashFlowModel[];
    public cashFlowChanges: Array<Partial<CashFlowModel>>;
    public barWidth: number;
    public barHeight: number;
    public heights: JaegerBarHeights[];
    public height: number;
    public topBar: Rect;
    // public bottomBar: Rect;
    public topBarColour: string;
    public bottomBarColour: string;
    public topBarType: string;
    public bottomBarType: string;
    public topLabel: string;
    public bottomLabel: string;
    public barMax: number;
    public bottomBarHide: Boolean = false;
    public barColumns: TLBarColumn[] = [];
    public bars: TLBar[];
    public parent: ProjectJaeger;
    // top bar decor
    public topLine: Line;
    public topBarMaxText: Konva.Text;
    public midLine: Line;
    public topLegendGroup: Group;
    public topDot: Circle;
    public topText: Konva.Text;
    public topImage: Group | Shape;
    public topInfoCollisionTarget: Shape;
    // bottom bar decor
    public bottomLegendGroup: Group;
    public bottomDot: Circle;
    public bottomText: Konva.Text;
    public bottomImage: Group | Shape;
    public bottomInfoCollisionTarget: Shape;
    public bottomLine: Line;
    public bottomBarMaxText: Konva.Text;
    private animations: Konva.Animation[];
    private readonly timelineService: TimelineService;
    private readonly barFactory: BarFactory;

    constructor(parent: ProjectJaeger) {
        super();
        this.timelineService = TimelineService.getInstance();
        this.barFactory = BarFactory.getInstance();
        this.parent = parent;
    }

    private configureBarArea(): void {
        this.height = (1 - this.parent.props.context.timelinePosition) * this.parent.props.height - 136;
        this.jaegerCashFlow = this.timelineService.getCashFlow();

        this.barWidth = JaegerService.getBarWidth(
            this.parent.props.context,
            this.parent.props.user,
            this.jaegerCashFlow
        );

        this.barHeight = this.barAreaGroup.height() / 2 - 4;
        this.heights = JaegerService.getBars(this.jaegerCashFlow);

        switch (this.parent.props.view) {
            case 'networth':
                this.topBarColour = colours.netWorthBar;
                this.topBarType = 'netWorth';
                this.barMax = this.heights[0].netWorthMax;
                this.topLabel = 'My net worth';
                this.bottomBarHide = true;
                break;
            case 'investment-debt':
                this.topBarColour = this.parent.props.assetsColor ? colours.investmentBar : colours.neutralBar;
                this.bottomBarColour = colours.debtBar;
                this.topBarType = 'investment';
                this.bottomBarType = 'debt';
                this.topLabel = 'My assets';
                this.bottomLabel = 'My debts';
                this.barMax = Math.max(this.heights[0].investmentMax, this.heights[0].debtMax);
                this.bottomBarHide = false;
                break;
            case 'income-expense':
            default:
                this.topBarColour = this.parent.props.incomeColor ? colours.incomeBar : colours.neutralBar;
                this.bottomBarColour = this.parent.props.expensesColor ? colours.expenseBar : colours.neutralBar;
                this.topBarType = 'income';
                this.bottomBarType = 'expense';
                this.topLabel = 'My income';
                this.bottomLabel = 'My expenses';
                this.barMax = Math.max(this.heights[0].incomeMax, this.heights[0].expenseMax);
                this.bottomBarHide = false;
        }
    }

    private upsertTopLine(): void {
        const points: number[] = [
            0,
            this.parent.timeline.timelineY + TOP_LEGEND_OFFSET,
            this.parent.stage.width() * this.parent.props.context.magnification,
            this.parent.timeline.timelineY + TOP_LEGEND_OFFSET
        ];
        if (this.topLine) {
            this.topLine.points(points);
        } else {
            this.topLine = new Line({
                points,
                stroke: colours.legendLine,
                strokeWidth: 1,
                perfectDrawEnabled: false,
                shadowForStrokeEnabled: false,
                bwType: 'topLine'
            });
            this.parent.mainLayer.add(this.topLine);
        }
    }

    private upsertTopBarMaxText(): void {
        const y: number = this.parent.timeline.timelineY + TOP_MAX_TEXT_OFFSET;
        const text: string = FormatService.formatCompactMoney(this.barMax);
        if (this.topBarMaxText) {
            this.topBarMaxText.y(y);
            this.topBarMaxText.text(text);
        } else {
            this.topBarMaxText = new Konva.Text({
                x: 20,
                y,
                text,
                fontSize: TIMELINE_FONT_SIZE_SMALL,
                lineHeight: 1.5,
                fontFamily: TIMELINE_LEGEND_FONT,
                fill: colours.textWeak,
                perfectDrawEnabled: false,
                shadowForStrokeEnabled: false
            });
            this.parent.fixedLayer.add(this.topBarMaxText);
        }
    }

    private upsertMidLine(): void {
        const midY = this.parent.timeline.timelineY + TOP_LEGEND_OFFSET + this.height / 2 - 2;
        const points: number[] = [0, midY, this.parent.stage.width() * this.parent.props.context.magnification, midY];
        if (this.midLine) {
            this.midLine.points(points);
        } else {
            this.midLine = new Line({
                points,
                stroke: colours.legendLine,
                strokeWidth: 1,
                perfectDrawEnabled: false,
                shadowForStrokeEnabled: false,
                bwType: 'midLine'
            });
            this.parent.mainLayer.add(this.midLine);
        }
    }

    private upsertTopGroup(): void {
        const y: number = this.parent.timeline.timelineY + 54;

        if (this.topLegendGroup) {
            this.topLegendGroup.y(y);
        } else {
            this.topLegendGroup = new Konva.Group({
                id: 'topLegendGroup',
                x: 26,
                y,
                perfectDrawEnabled: false
            });
        }
    }

    private upsertTopDot(): void {
        if (!this.topDot && this.topLegendGroup) {
            this.topDot = new Circle({
                x: 0,
                y: 12,
                width: 12,
                fill: this.topBarColour,
                perfectDrawEnabled: false,
                shadowForStrokeEnabled: false
            });

            this.topLegendGroup.add(this.topDot);
        }
    }

    private setInfoImage(text: Konva.Text, top: boolean = true): void {
        Konva.Image.fromURL(infoIcon, (imageNode: Group | Shape) => {
            imageNode.setAttrs({
                x: text.x() + text.width() + 8,
                y: text.y() + 4,
                width: 16,
                height: 16,
                willReadFrequently: true,
                perfectDrawEnabled: false
            });

            // the icon doesn't work well as a collision target, so we need to make one
            const target = new Konva.Circle({
                x: text.x() + text.width() + 16,
                y: text.y() + 12,
                width: 16,
                height: 16,
                perfectDrawEnabled: false,
                shadowForStrokeEnabled: false
            });

            if (top) {
                this.topImage = imageNode;
                this.topInfoCollisionTarget = target;
                this.topLegendGroup.add(this.topImage);
                this.topLegendGroup.add(this.topInfoCollisionTarget);
                this.topInfoCollisionTarget.moveToTop();
            } else {
                this.bottomImage = imageNode;
                this.bottomInfoCollisionTarget = target;
                this.bottomLegendGroup.add(this.bottomImage);
                this.bottomLegendGroup.add(this.bottomInfoCollisionTarget);
            }
        });
    }

    private upsertTopText(): void {
        if (this.topText) {
            this.topText.text(this.topLabel);
            this.topImage?.x(this.topText.x() + this.topText.width() + 8);
            this.topInfoCollisionTarget?.x(this.topText.x() + this.topText.width() + 16);
        } else if (!this.topText && this.topLegendGroup) {
            this.topText = new Konva.Text({
                x: 12,
                y: 0,
                text: this.topLabel,
                fontSize: 16,
                fontVariant: 'bold',
                lineHeight: 1.5,
                fontFamily: TIMELINE_LEGEND_FONT,
                fill: colours.labelText,
                perfectDrawEnabled: false,
                shadowForStrokeEnabled: false
            });

            this.topLegendGroup.add(this.topText);
            this.setInfoImage(this.topText);
        }
    }

    private decorateTopBar(): void {
        // make top lines
        this.upsertTopLine();
        this.upsertTopBarMaxText();
        this.upsertMidLine();
        // Make top legend
        this.upsertTopGroup();
        this.upsertTopDot();
        this.upsertTopText();
        this.parent.fixedLayer.add(this.topLegendGroup);
        // set listeners
        this.setMouseEnterListener(this.topInfoCollisionTarget);
        this.setMouseLeaveListener(this.topInfoCollisionTarget);
    }

    private upsertBottomGroup(): void {
        const y: number = this.parent.props.height - 38;

        if (this.bottomLegendGroup) {
            this.bottomLegendGroup.y(y);
        } else {
            this.bottomLegendGroup = new Konva.Group({
                id: 'bottomLegendGroup',
                x: 26,
                y,
                perfectDrawEnabled: false
            });
        }
    }

    private upsertBottomDot(): void {
        if (!this.bottomDot && this.bottomLegendGroup) {
            this.bottomDot = new Konva.Circle({
                x: 0,
                y: 12,
                width: 12,
                fill: this.bottomBarColour,
                perfectDrawEnabled: false,
                shadowForStrokeEnabled: false
            });

            this.bottomLegendGroup.add(this.bottomDot);
        }
    }

    private upsertBottomText(): void {
        if (this.bottomText) {
            this.bottomText.text(this.bottomLabel);
            this.bottomImage?.x(this.bottomText.x() + this.bottomText.width() + 8);
            this.bottomInfoCollisionTarget?.x(this.bottomText.x() + this.bottomText.width() + 16);
            if (this.bottomImage) {
                this.bottomImage.x(this.bottomText.x() + this.bottomText.width() + 8);
                this.bottomImage.y(4);
            }
        } else if (this.bottomLegendGroup && !this.bottomText) {
            this.bottomText = new Konva.Text({
                x: 12,
                y: 0,
                text: this.bottomLabel,
                fontSize: 16,
                fontVariant: 'bold',
                lineHeight: 1.5,
                fontFamily: TIMELINE_LEGEND_FONT,
                fill: colours.labelText,
                perfectDrawEnabled: false,
                shadowForStrokeEnabled: false
            });

            this.bottomLegendGroup.add(this.bottomText);
            this.setInfoImage(this.bottomText, false);
        }
    }

    private upsertBottomLine(): void {
        const points: number[] = [
            0,
            this.parent.props.height - 56,
            this.parent.stage.width() * this.parent.props.context.magnification,
            this.parent.props.height - 56
        ];
        if (this.bottomLine) {
            this.bottomLine.points(points);
        } else {
            this.bottomLine = new Line({
                points,
                stroke: colours.legendLine,
                strokeWidth: 1,
                perfectDrawEnabled: false,
                shadowForStrokeEnabled: false,
                bwType: 'bottomLine'
            });
            this.parent.mainLayer.add(this.bottomLine);
        }
    }

    private upsertBottomBarMaxText(): void {
        const y: number = this.parent.props.height - BOTTOM_MAX_TEXT_OFFSET;
        const text: string = FormatService.formatCompactMoney(this.barMax);
        if (this.bottomBarMaxText) {
            this.bottomBarMaxText.y(y);
            this.bottomBarMaxText.text(text);
        } else {
            this.bottomBarMaxText = new Konva.Text({
                x: 20,
                y,
                text,
                fontSize: TIMELINE_FONT_SIZE_SMALL,
                lineHeight: 1.5,
                fontFamily: TIMELINE_LEGEND_FONT,
                fill: colours.textWeak,
                perfectDrawEnabled: false,
                shadowForStrokeEnabled: false
            });
            this.parent.fixedLayer.add(this.bottomBarMaxText);
        }
    }

    private decorateBottomBar(): void {
        // legend group
        this.upsertBottomGroup();
        this.upsertBottomDot();
        this.upsertBottomText();
        this.upsertBottomLine();
        this.parent.fixedLayer.add(this.bottomLegendGroup);
        // max text
        this.upsertBottomBarMaxText();
        // listeners
        this.setMouseEnterListener(this.bottomInfoCollisionTarget);
        this.setMouseLeaveListener(this.bottomInfoCollisionTarget);
    }

    private createGroup(): void {
        if (this.barAreaGroup) {
            this.barAreaGroup.width(this.parent.stage.width());
            this.barAreaGroup.height(this.parent.stage.height());
            this.barAreaGroup.y(this.parent.timeline.timelineY + TOP_LEGEND_OFFSET);
        } else {
            this.barAreaGroup = new Group({
                id: 'barAreaGroup',
                x: 0,
                y: this.parent.timeline.timelineY + TOP_LEGEND_OFFSET,
                width: this.parent.stage.width(),
                height: this.height,
                isBar: true,
                bwType: 'bar',
                perfectDrawEnabled: false
            });
        }
    }

    private setMouseEnterListener(target: Shape): void {
        target?.off(JaegerEvent.MOUSE_ENTER);
        target?.on(JaegerEvent.MOUSE_ENTER, () => {
            const targetAbsPos: Konva.Vector2d = target.absolutePosition();
            this.parent.props.showLegendBox(target === this.topInfoCollisionTarget, targetAbsPos.x, targetAbsPos.y);
        });
    }

    private setMouseLeaveListener(target: Shape): void {
        target?.off(JaegerEvent.MOUSE_LEAVE);
        target?.on(JaegerEvent.MOUSE_LEAVE, () => {
            const mousePosInterval = setInterval(() => {
                const mousePosition: Konva.Vector2d = target.getRelativePointerPosition();
                if (
                    target.getRelativePointerPosition().y < -221 ||
                    mousePosition.y > 20 ||
                    Math.abs(mousePosition.x) > 20
                ) {
                    this.parent.props.hideLegendBox();
                    clearInterval(mousePosInterval);
                }
            }, 10);
        });
    }

    public add(element: Group | Shape): void {
        if (this.barAreaGroup) {
            this.barAreaGroup.add(element);
            return;
        }

        console.error('Bar area group not found.');
    }

    public updateLimits(): void {
        this.configureBarArea();
        this.decorateTopBar();
        if (!this.bottomBarHide) this.decorateBottomBar();
    }

    public updateBarColors(barColumn: TLBarColumn, changes: Partial<CashFlowModel>): void {
        if (changes) {
            let topChanged;
            let bottomChanged = false;
            const oldTopFill = barColumn.topBar.fill;
            const oldBottomFill = barColumn.bottomBar.fill;
            switch (this.parent.props.view) {
                case 'networth':
                    barColumn.topBar.fill = changes.netWorth !== 0 ? colours.netWorthBar : colours.neutralBar;
                    topChanged = oldTopFill !== barColumn.topBar.fill;
                    break;
                case 'investment-debt':
                    barColumn.topBar.fill = changes.investment !== 0 ? colours.investmentBar : colours.neutralBar;
                    barColumn.bottomBar.fill = changes.debt !== 0 ? colours.debtBar : colours.neutralBar;
                    topChanged = oldTopFill !== barColumn.topBar.fill;
                    bottomChanged = oldBottomFill !== barColumn.bottomBar.fill;
                    break;
                case 'income-expense':
                default:
                    barColumn.topBar.fill = changes.income !== 0 ? colours.incomeBar : colours.neutralBar;
                    barColumn.bottomBar.fill = changes.expense !== 0 ? colours.expenseBar : colours.neutralBar;
                    topChanged = oldTopFill !== barColumn.topBar.fill;
                    bottomChanged = oldBottomFill !== barColumn.bottomBar.fill;
            }
            if (topChanged) this.barFactory.build(barColumn.topBar);
            if (bottomChanged) this.barFactory.build(barColumn.bottomBar);
        } else {
            barColumn.topBar.fill = this.topBarColour;
            barColumn.topBar.barRect.fill(barColumn.topBar.fill);
            barColumn.bottomBar.fill = this.bottomBarColour;
            barColumn.bottomBar.barRect.fill(barColumn.bottomBar.fill);
        }
    }

    public upsert(): void {
        this.createGroup();
        this.configureBarArea();
        this.decorateTopBar();
        if (!this.bottomBarHide) {
            this.decorateBottomBar();
            this.bottomLegendGroup.show();
            this.bottomBarMaxText.show();
            this.bottomLine.show();
        } else {
            this.bottomLegendGroup.hide();
            this.bottomBarMaxText.hide();
            this.bottomLine.hide();
        }

        this.heights.forEach((height: JaegerBarHeights, index: number) => {
            let barColumn = this.barColumns[index];
            if (barColumn) {
                barColumn.upsert(height, index);
            } else {
                barColumn = new TLBarColumn(this, height, index);
                this.barColumns[index] = barColumn;
                barColumn.upsert();
                this.add(barColumn.topBar.barGroup);
                this.add(barColumn.bottomBar.barGroup);
            }
            this.updateBarColors(barColumn, this.cashFlowChanges?.[index]);
            barColumn.topBar.show();
            if (this.bottomBarHide) {
                barColumn.bottomBar.hide();
            } else {
                barColumn.bottomBar.show();
            }
        });

        // clear extra barColumns if they are left over when timeline was longer
        this.barColumns.slice(this.heights.length).forEach((barColumn) => {
            barColumn.topBar.hide();
            barColumn.bottomBar.hide();
        });
    }

    public create(): void {
        this.upsert();
    }

    public destroy(): void {
        // destroy all the pieces
        this.barColumns.forEach((bc: TLBarColumn) => {
            bc.destroy();
        });
        this.topLine.destroy();
        this.topBarMaxText.destroy();
        this.midLine.destroy();
        this.topDot.destroy();
        this.topText.destroy();
        this.bottomDot.destroy();
        this.bottomText.destroy();
        this.bottomLine.destroy();
        this.bottomBarMaxText.destroy();
        this.barAreaGroup.destroy();

        // forget about the pieces so we can recreate
        this.barColumns = [];
        this.topLine = null;
        this.topBarMaxText = null;
        this.midLine = null;
        this.topDot = null;
        this.topText = null;
        this.bottomDot = null;
        this.bottomText = null;
        this.bottomLine = null;
        this.bottomBarMaxText = null;
        this.barAreaGroup = null;
    }

    public remove(): void {}

    public updateX(): void {
        this.barWidth = this.parent.barWidth;
        this.barColumns.forEach((bc: TLBarColumn) => bc.updateX());
    }

    public updateY(): void {}

    public changedCashFlows(cashFlow1: CashFlowModel[], cashFlow2: CashFlowModel[]): Array<Partial<CashFlowModel>> {
        const startDate1 = cashFlow1[0].startDate;
        const startDate2 = cashFlow2[0].startDate;
        if (!startDate1.equals(startDate2)) {
            return null;
        }
        this.cashFlowChanges = cashFlow2.map((cf2, index) => {
            if (index >= cashFlow1.length) {
                return {
                    income: 0,
                    expense: 0,
                    investment: 0,
                    debt: 0,
                    netWorth: 0
                };
            } else {
                const cf1 = cashFlow1[index];
                return {
                    startDate: cf2.startDate,
                    income: cf2.income === cf1.income ? 0 : 1,
                    expense: cf2.expense === cf1.expense ? 0 : 1,
                    investment: cf2.investment === cf1.investment ? 0 : 1,
                    debt: cf2.debt === cf1.debt ? 0 : 1,
                    netWorth: cf2.netWorth === cf1.netWorth ? 0 : 1
                };
            }
        });
    }

    public stopAnimation(): void {
        if (this.animations) {
            this.animations.forEach((anim) => anim.stop());
            this.animations = null;
        }
    }

    public animate(oldCashFlow: CashFlowModel[], newCashFlow: CashFlowModel[]): void {
        if (!this.animations) {
            this.animations = [];
        } else {
            this.animations.filter((anim) => anim.isRunning()).forEach((anim) => anim.stop());
            this.animations = [];
        }
        const maxHeight: number =
            ((1 - this.parent.props.context.timelinePosition) * this.parent.props.height - 136) / 2;
        const prevHeights: JaegerBarHeights[] = JaegerService.getBars(oldCashFlow);
        const newHeights: JaegerBarHeights[] = JaegerService.getBars(newCashFlow);
        const period = 250;

        this.barColumns
            // only include columns that are visible now
            .forEach((barColumn: TLBarColumn, index: number) => {
                this.updateBarColors(barColumn, this.cashFlowChanges?.[index]);

                const topBarType: string = _.get(barColumn, 'topBar.type');
                const bottomBarType: string = _.get(barColumn, 'bottomBar.type');
                let prevHeightTop: number = Number(_.get(prevHeights[index], topBarType)) * maxHeight;
                let prevHeightBottom: number = Number(_.get(prevHeights[index], bottomBarType)) * maxHeight;
                const newHeightTop: number = Number(_.get(newHeights[index], topBarType)) * maxHeight;
                const newHeightBottom: number = Number(_.get(newHeights[index], bottomBarType)) * maxHeight;
                const topBarNode: Group = _.get(barColumn, 'topBar.barGroup.children[1]');
                const bottomBarNode: Group = _.get(barColumn, 'bottomBar.barGroup.children[1]');
                // update bar height numbers for automation and testing
                (window as any)[`${index}-${topBarType}-height`] = newHeightTop;
                (window as any)[`${index}-${bottomBarType}-height`] = newHeightBottom;

                // important for NSZ
                if (isNaN(prevHeightTop)) prevHeightTop = 0;
                if (isNaN(prevHeightBottom)) prevHeightBottom = 0;

                const anim: Konva.Animation = new Konva.Animation((frame: IFrame | undefined) => {
                    const getInterval = (
                        prevHeight: number,
                        newHeight: number
                    ): { interval: number; hitLimit: boolean } => {
                        let interval: number;
                        let hitLimit: boolean;

                        if (prevHeight > newHeight) {
                            const delta: number = prevHeight - newHeight;
                            interval = Math.max(prevHeight - delta * (frame.time / period), newHeight);
                            hitLimit = interval <= newHeight;
                        } else if (prevHeight < newHeight) {
                            const delta: number = newHeight - prevHeight;
                            interval = Math.min(prevHeight + delta * (frame.time / period), newHeight);
                            hitLimit = interval >= newHeight;
                        } else {
                            interval = newHeight;
                            hitLimit = true;
                        }

                        return { interval, hitLimit };
                    };

                    const top: { interval: number; hitLimit: boolean } = getInterval(prevHeightTop, newHeightTop);
                    const bottom: { interval: number; hitLimit: boolean } = getInterval(
                        prevHeightBottom,
                        newHeightBottom
                    );

                    if (topBarNode && !top.hitLimit) topBarNode.height(top.interval * -1);
                    if (bottomBarNode && !bottom.hitLimit) bottomBarNode.height(bottom.interval);
                    if ((top.hitLimit || !topBarNode) && (bottom.hitLimit || !bottomBarNode)) {
                        anim.stop();
                        if (topBarNode && !isNaN(newHeightTop)) topBarNode.height(newHeightTop * -1);
                        if (bottomBarNode && !isNaN(newHeightBottom)) bottomBarNode.height(newHeightBottom);
                    }
                }, this.parent.mainLayer);
                this.animations.push(anim);
            });

        this.animations.forEach((animation: Konva.Animation) => animation.start());
    }
}
